import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CelebrationIcon from '@mui/icons-material/Celebration';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import moment from 'moment';
import React, { FC, Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import strings from '../../../common/Translation/Translate';
import MultiSelect from '../../../components/MultiSelect/MultiSelect';
import axiosInstance from '../../../utils/axios';
import { rfqStatuses } from '../../../utils/constants/data';
import { routes } from '../../../utils/constants/routes';
import { URLS } from '../../../utils/constants/urls';
import { createDropdownData } from '../../../utils/validators/HelperFunctions';

interface RfqBookingProps {
  title: string;
  pageResponse: any;
  editPrivileges: boolean;
  getList: () => void;
  type: string;
  maxBodyHeight?: number;
}
const RfqBooking: FC<RfqBookingProps> = ({ getList, title, pageResponse, editPrivileges, type, maxBodyHeight = 280 }) => {
  const theme = useTheme();
  const Navigate = useNavigate();
  const [pageResponseList, setPageResponseList] = useState<any>([]);
  const iconsStyle = {
    fontSize: '16px',
    position: 'relative',
    top: 2,
    marginRight: 1,
  };

  useEffect(() => {
    const val = type === 'rfq' ? 'status' : 'action';
    const a = pageResponse.map((response: any) => ({
      ...response,
      [val]: {
        value: response[val]?.value,
        label: rfqStatuses(strings).find((i: any) => i.id === response[val]?.value)?.name,
      },
    }));
    setPageResponseList(a);
  }, [pageResponse, type]);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Ignore copy failures (permissions / http)
    }
  };

  const urgencyChip = (startDate: any) => {
    const today = moment().startOf('day');
    const start = moment(startDate).startOf('day');
    const diff = start.diff(today, 'days');

    if (diff < 0) return null;
    if (diff === 0) return { label: 'TODAY', bg: '#FEE2E2', fg: '#991B1B' };
    if (diff === 1) return { label: 'TOMORROW', bg: '#FFEDD5', fg: '#9A3412' };
    if (diff <= 3) return { label: `IN ${diff} DAYS`, bg: '#FEF9C3', fg: '#854D0E' };
    return null;
  };

  const handleTaskSelection = async (value: any, id: number) => {
    const sendRfq = {
      status: value.value,
    };
    const url = type === 'rfq' ? URLS.dashboard_rfq_task(id) : URLS.dashboard_update_task(id);
    const { status } = await axiosInstance.put(url, sendRfq);
    if (status === 200) {
      const pageArray = [...pageResponseList];
      for (const element of pageArray) {
        if (id === element.id) {
          if (type === 'rfq') element.status = value;
          else element.action = value;
        }
      }
      setPageResponseList(pageArray);
      if (value.label === 'CLOSED') {
        getList();
      }
    }
  };

  return (
    <Card
      sx={{
        borderRadius: 2,
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}>
      <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography fontWeight={600} sx={{ fontSize: '1rem' }}>{title}</Typography>
      </Box>
      <CardContent sx={{ p: 0, pb: 0, maxHeight: maxBodyHeight, overflowY: 'auto', flex: 1 }}>
        {/* <Scrollbar style={{ width: '100%', height: '60vh', fontFamily: theme.typography.fontFamily }}> */}
        <List
          sx={{
            width: '100%',
            bgcolor: 'background.paper',
            pt: 0,
          }}>
          {pageResponseList?.length > 0 ? (
            pageResponseList.map((item: any) => (
              <Fragment key={item.id}>
                <ListItem alignItems="flex-start" key={item.id}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                        {type === 'rfq' ? (
                          <Link
                            onClick={() => Navigate(`${routes.viewRfq}/${item.id}`)}
                            sx={{ fontSize: '16px' }}
                            component="button">
                            {' '}
                            {item.name}{' '}
                          </Link>
                        ) : (
                          item.name
                        )}
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                          {type === 'rfq' && item.preferredStartDate ? (() => {
                            const u = urgencyChip(item.preferredStartDate);
                            if (!u) return null;
                            return (
                              <Chip
                                size="small"
                                label={u.label}
                                sx={{
                                  bgcolor: u.bg,
                                  color: u.fg,
                                  fontWeight: 800,
                                  letterSpacing: 0.4,
                                }}
                              />
                            );
                          })() : null}
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              borderRadius: 1,
                              px: 0.75,
                              py: 0.25,
                              transition: 'background-color 160ms ease',
                              '&:hover': { backgroundColor: '#eef2ff' },
                            }}>
                            <Link
                              onClick={() => {
                                type === 'rfq'
                                  ? Navigate(`${routes.viewRfq}/${item.id}`)
                                  : Navigate(`${routes.bookings}/${item.bookingId}`);
                              }}>
                              <Typography
                                ml={1}
                                fontWeight={800}
                                sx={{ color: theme.palette.success.main, '&:hover': { textDecoration: 'underline' } }}
                                variant="caption">
                                {type === 'rfq' ? item.venue : item.bookingReference}
                                {type === 'rfq' && item.guests ? ` · ${item.guests} pax` : ''}
                              </Typography>
                            </Link>
                            {(type === 'rfq' ? item.venue : item.bookingReference) ? (
                              <Tooltip title="Copy" arrow>
                                <IconButton
                                  size="small"
                                  onClick={(e: any) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    copyToClipboard(String(type === 'rfq' ? item.venue : item.bookingReference));
                                  }}
                                  sx={{
                                    ml: 0.25,
                                    p: 0.25,
                                    color: 'text.secondary',
                                    '&:hover': { color: 'text.primary' },
                                  }}>
                                  <ContentCopyRoundedIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                          </Box>
                        </Box>
                      </Box>
                    }
                    secondary={
                      <React.Fragment>
                        <Typography my={1} variant="body2" color="text.primary">
                          <CalendarMonthIcon sx={iconsStyle} />
                          {type === 'rfq'
                            ? `${moment(item.preferredStartDate).format('DD/MM/YYYY')} - ${moment(
                                item.preferredEndDate,
                              ).format('DD/MM/YYYY')}`
                            : `${moment(item.startDate).format('DD/MM/YYYY')} - ${moment(item.endDate).format(
                                'DD/MM/YYYY',
                              )}`}
                        </Typography>
                        <Typography variant="subtitle2">
                          {item.mobile}
                          {item.email && (
                            <>
                              {' '}
                              ,{' '}
                              <Typography component="span" variant="body2" color="text.primary">
                                {item.email}
                              </Typography>
                            </>
                          )}
                        </Typography>
                        <Typography variant="subtitle2" mt={1} mb={1}>
                          <CelebrationIcon sx={iconsStyle} />
                          {type === 'rfq' ? item.eventType : item.event}
                          {item.timeSlot && (
                            <>
                              {' '}
                              - <AccessTimeIcon sx={iconsStyle} />
                              <Typography component="span" variant="body2" color="text.primary">
                                {item.timeSlot}
                              </Typography>
                            </>
                          )}
                        </Typography>
                        {editPrivileges && (
                          <div
                            title={type === 'rfq' ? item.status.value : item.action.value}
                            className={`col-md-4 text-center ${
                              type === 'rfq' ? item?.status?.value : item?.action?.value
                            }`}>
                            <MultiSelect
                              className="status-drodown"
                              inputValue={type === 'rfq' ? item.status : item.action}
                              onChange={(value: any) => handleTaskSelection(value, item.id)}
                              options={createDropdownData(rfqStatuses(strings), ['id', 'name'])}
                              isClearable={false}
                              field="status"
                              id={item.id}
                            />
                          </div>
                        )}
                      </React.Fragment>
                    }
                  />
                </ListItem>
                <Divider component="li" />
              </Fragment>
            ))
          ) : (
            <Box
              sx={{
                py: 8,
                textAlign: 'center',
                color: 'text.secondary',
              }}>
              <Typography sx={{ fontSize: 14, fontWeight: 800 }}>
                No RFQs for the selected period.
              </Typography>
              <Typography sx={{ mt: 1, fontSize: 13 }}>
                Check back later or review older RFQs from the RFQ list.
              </Typography>
            </Box>
          )}
        </List>
        {/* </Scrollbar> */}
      </CardContent>
    </Card>
  );
};
export default RfqBooking;
